// ./src/components/features/Events/eventZone.ts
//
// The event editors' pickers are wall clocks in the EVENT's time zone (2026-10-04 — they used to be the host browser's,
// while the public site, the door and the time rules read them in the event's: a Berlin host's all-day New York event
// showed a day early). When this device keeps another zone, the editor says so under the time zone field — otherwise a
// host who typed 6 PM would wonder why their own calendar shows midnight.
// ⓘ Pure (no React): `check-event-zone` runs it.
import type { TFunction } from 'i18next';

import { browserZoneDiffers } from '@em/utils';

/** The browser's own time zone, or null when it cannot be read. */
export const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/**
 * The line an event editor shows about its pickers' zone: null when this device is in the event's zone (nothing to say).
 * `field` — under the time zone field itself ("this time zone"); `elsewhere` — where the zone is not on the form (a date's
 * drawer: "the event's time zone"); `day` — a DATE with no time (a closing day), which is a day on the event's calendar.
 */
export const eventZoneNote = (t: TFunction, tz: string | null | undefined, where: 'field' | 'elsewhere' | 'day' = 'field'): string | null => {
  if (!browserZoneDiffers(tz)) {
    return null;
  }

  const device = browserTimeZone() ?? '—';

  if (where === 'day') {
    return t('events:details.zoneNoteDay', { defaultValue: "The day is on the event's calendar ({{tz}}) — your device is set to {{device}}.", tz, device });
  }

  return where === 'field'
    ? t('events:details.zoneNote', { defaultValue: 'Dates and times here are in this time zone ({{tz}}) — your device is set to {{device}}.', tz, device })
    : t('events:details.zoneNoteElsewhere', { defaultValue: "Times are in the event's time zone ({{tz}}) — your device is set to {{device}}.", tz, device });
};
